import {type Directive, type ObjectDirective, watch} from 'vue';
import {intl, translationsLoadedPromise, areTranslationsLoaded} from '../utilities/translations'
import {evaluateResultWithContext} from "@/utilities/nonDomUtils";
import type {ComponentPublicInstance, RendererElement, RendererNode, VNode} from "@vue/runtime-core";

declare type DirectiveModifiers = Record<string, boolean>;
interface TranslateDirectiveBinding<V=any>  {
    instance: ComponentPublicInstance | null;
    value: V;
    oldValue: V | null;
    arg?: string;
    modifiers: DirectiveModifiers;
    dir: TranslateDirective<any, V>;
}
type TranslatedHTMLElement = HTMLElement & {
    translations: {
        key:string,
        value: string
    }
};
declare type NfProps = {
    translationKey?: string,
    translationScript?: string,
    preTranslationWhiteSpace: string,
    postTranslationWhiteSpace: string,
    watchUnsubscribe?: () => void;
};
declare type translationVNode = VNode< RendererNode,  RendererElement> & {nfprops: NfProps};

interface TranslateDirective <T = any, V = any> extends Omit<ObjectDirective<T, V>, 'mounted'|'created'|'beforeUnmount'|'updated'|'beforeMount'|'beforeUpdate'> {
    mounted?: (this:void,el: T, binding: TranslateDirectiveBinding<V>, vnode: translationVNode, prevVNode: VNode<any, T> | null) => void,
    beforeUpdate?: (this:void,el: T, binding: TranslateDirectiveBinding<V>, vnode: translationVNode, prevVNode: translationVNode ) => void,
    updated?: (this:void,el: T, binding: TranslateDirectiveBinding<V>, vnode: translationVNode, prevVNode: translationVNode ) => void,
    created?: (this:void,el: T, binding: TranslateDirectiveBinding<V>, vnode: translationVNode, prevVNode: VNode<any, T> | null) => void,
    beforeUnmount?: (this:void,el: T, binding: TranslateDirectiveBinding<V>, vnode: translationVNode, prevVNode: VNode<any, T> | null) => void,
    beforeMount?: (this:void,el: T, binding: TranslateDirectiveBinding<V>, vnode: translationVNode, prevVNode: VNode<any, T> | null) => void,
}


function setTranslation(el: TranslatedHTMLElement, translationKey: string, translationValues: any, vnode: translationVNode) {
    if (!translationKey?.length) {
        el.innerText = 'Błąd tłumaczenia';
        return;
    }
    el.translations = {
        key: translationKey,
        value: translationValues
    };
    el.innerText = vnode.nfprops.preTranslationWhiteSpace
        + intl.formatMessage({id: translationKey, defaultMessage: translationKey}, translationValues)
        + vnode.nfprops.postTranslationWhiteSpace;
}

/**
 * Dyrektywa translacji, używana jako atrybut 'translate'
 * Tekst elementu, w którym jest użyta ta dyrektywa będzie brany jako klucz translacji
 * Użycie: <span v-translate>Klucz do tłumaczeń</span>
 * Wartość atrybutu może zostać zbindowana i może podawać wartości w translacji
 * Np. <span v-translate="{ilosc: 5}">Klucz</span> gdzie Klucz = "{ilosc} dni"
 */
export default {
    /**
     *
     * @param {HTMLElement} el
     * @param binding
     */
    beforeMount(el: TranslatedHTMLElement, binding: TranslateDirectiveBinding<any>, vnode: translationVNode) {
        let translationValues = binding.value;
        let translationKey = el.innerText;
        if (!vnode.nfprops) {
            vnode.nfprops = {
                preTranslationWhiteSpace: '',
                postTranslationWhiteSpace: '',
            };
        }
        if(el.hasAttribute('translation-key')) {
            translationKey = el.getAttribute('translation-key') || translationKey;
            vnode.nfprops.translationKey = translationKey;
        } else if (el.hasAttribute('translation-key-script')) {
            vnode.nfprops.translationScript = el.getAttribute('translation-key-script') || '';
            vnode.nfprops.watchUnsubscribe = watch(() => evaluateResultWithContext(el.getAttribute('translation-key-script'), binding.instance),
                () => {
                    setTranslation(el, evaluateResultWithContext(vnode.nfprops.translationScript!, binding.instance), binding.value, vnode);
                })
            translationKey = evaluateResultWithContext(el.getAttribute('translation-key-script'), binding.instance);
        } else {
            vnode.nfprops.preTranslationWhiteSpace = translationKey.match(/^\s+/)?.[0] || '';
            vnode.nfprops.postTranslationWhiteSpace = translationKey.match(/\s+$/)?.[0] || '';
            translationKey = translationKey.trim();
        }
        setTranslation(el, translationKey, translationValues, vnode);
        if(!areTranslationsLoaded()) {
            translationsLoadedPromise.then(() => {
                translationValues = binding.value;
                if (vnode.nfprops.translationScript) {
                    if (vnode.nfprops.watchUnsubscribe) {
                        vnode.nfprops.watchUnsubscribe();
                    }
                    translationKey = evaluateResultWithContext(vnode.nfprops.translationScript, binding.instance);
                    vnode.nfprops.watchUnsubscribe = watch(() => evaluateResultWithContext(el.getAttribute('translation-key-script'), binding.instance),
                        () => {
                            setTranslation(el, evaluateResultWithContext(vnode.nfprops.translationScript!, binding.instance), binding.value, vnode);
                        })
                }
                setTranslation(el, translationKey, translationValues, vnode);
                //@ts-ignore
                binding.instance.$forceUpdate();
            })
        }
    },
    updated: (el: TranslatedHTMLElement, binding, node, prevNode) => {
        node.nfprops = prevNode.nfprops;
    },
    beforeUpdate: (el: TranslatedHTMLElement, binding, node, prevNode) => {
        node.nfprops = prevNode.nfprops;
        if(binding.value && areTranslationsLoaded() && binding.value != binding.oldValue) {
            const translationValues = binding.value;
            let translationKey = el.innerText;
            if (node.nfprops.translationKey) {
                translationKey = node.nfprops.translationKey;
            } else if (node.nfprops.translationScript) {
                if (node.nfprops.watchUnsubscribe) {
                    node.nfprops.watchUnsubscribe();
                }
                translationKey = evaluateResultWithContext(node.nfprops.translationScript, binding.instance);
                node.nfprops.watchUnsubscribe = watch(() => evaluateResultWithContext(el.getAttribute('translation-key-script'), binding.instance),
                    () => {
                        setTranslation(el, evaluateResultWithContext(node.nfprops.translationScript!, binding.instance), binding.value, node);
                    })
            }
            setTranslation(el, translationKey, translationValues, node);
        }
    }
} as TranslateDirective<TranslatedHTMLElement>;